import { useEffect, useState } from 'react';
import api from '../utils/api';
import { AlertTriangle, Package, Check, X, RefreshCw, TrendingDown } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const SEVERITY_COLOR = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/40',
  warning: 'bg-orange-500/20 text-orange-400 border-orange-500/40',
  info: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
};

const SEVERITY_LABEL = {
  critical: '긴급',
  warning: '경고',
  info: '정보',
};

export default function MaterialsPage() {
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => {
    Promise.all([
      api.get('/materials/suggestions'),
      api.get('/materials/forecast/summary'),
    ]).then(([s, sum]) => {
      setSuggestions(s.data);
      setSummary(sum.data);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runForecast = async () => {
    setRunning(true);
    try {
      await api.post('/materials/forecast/run');
      load();
    } catch (e) {
      alert('예측 실패: ' + (e.response?.data?.message || e.message));
    } finally {
      setRunning(false);
    }
  };

  const resolve = async (id, status) => {
    if (!confirm(status === 'ordered' ? '발주 완료로 처리할까요?' : '이 제안을 삭제할까요?')) return;
    await api.post(`/materials/suggestions/${id}/resolve`, { status });
    load();
  };

  return (
    <div>
      <PageHeader icon={Package} title="자재 예측 · 발주 알림">
        <button
          onClick={runForecast}
          disabled={running}
          className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg text-sm disabled:opacity-50"
        >
          <RefreshCw size={14} className={running ? 'animate-spin' : ''} />
          지금 예측 실행
        </button>
      </PageHeader>

      {/* 요약 카드 */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Package size={16} className="text-gray-500" />
              <span className="text-gray-400 text-sm">전체 잔량</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {(summary.totalGram / 1000).toFixed(1)}
              <span className="text-lg text-gray-500 ml-1">kg</span>
            </p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-red-400" />
              <span className="text-gray-400 text-sm">긴급</span>
            </div>
            <p className="text-3xl font-bold text-red-400">{summary.critical}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-orange-400" />
              <span className="text-gray-400 text-sm">경고</span>
            </div>
            <p className="text-3xl font-bold text-orange-400">{summary.warning}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown size={16} className="text-blue-400" />
              <span className="text-gray-400 text-sm">정보</span>
            </div>
            <p className="text-3xl font-bold text-blue-400">{summary.info}</p>
          </div>
        </div>
      )}

      {/* 제안 목록 */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h2 className="text-white font-semibold text-sm mb-4">재발주 제안</h2>
        {suggestions.length === 0 && (
          <div className="text-center py-8">
            <Check className="mx-auto text-green-400 mb-2" size={32} />
            <p className="text-gray-400 text-sm">현재 발주가 필요한 자재가 없습니다</p>
          </div>
        )}
        <div className="space-y-2">
          {suggestions.map(s => (
            <div
              key={s.id}
              className={`flex items-center gap-4 border rounded-lg p-4 ${SEVERITY_COLOR[s.severity] || 'bg-gray-800 border-gray-700'}`}
            >
              <div className="flex-shrink-0">
                <AlertTriangle size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-black/30">
                    {SEVERITY_LABEL[s.severity]}
                  </span>
                  <span className="text-white font-semibold">
                    {s.material}
                    {s.colorName && <span className="text-gray-300"> · {s.colorName}</span>}
                  </span>
                </div>
                <div className="text-xs text-gray-300 space-x-3">
                  <span>현재 잔량: <b>{s.currentGram}g</b></span>
                  {s.daysUntilOut != null && <span>예상 소진: <b>{s.daysUntilOut}일 후</b></span>}
                  <span>예상 부족: <b>{s.projectedShortfallGram}g</b></span>
                  <span>권장 주문: <b>{s.suggestedSpools}스풀</b></span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {dayjs(s.createdAt).format('YYYY-MM-DD HH:mm')} 생성
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => resolve(s.id, 'ordered')}
                  className="px-3 py-1.5 bg-green-600/30 hover:bg-green-600/50 text-green-300 rounded text-xs font-medium"
                >
                  발주 완료
                </button>
                <button
                  onClick={() => resolve(s.id, 'dismissed')}
                  className="px-3 py-1.5 bg-gray-700/50 hover:bg-gray-700 text-gray-400 rounded text-xs"
                >
                  무시
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
